import { useEffect, useRef, useState, type FormEvent } from 'react';
import { t } from '../i18n/idioma';
import { useSession } from '../state/SessionContext';

/** As classes de cada lugar onde o passo aparece: a tela de entrada e o cartão da landing aberta. */
const CLASSES = {
  tela: {
    form: 'pilha', campo: 'campo', rotulo: 'campo__rotulo', caixa: 'campo__caixa', botao: 'btn btn--primary',
    elo: 'elo', erro: 'aviso', miudo: 'footnote'
  },
  landing: {
    form: 'lp-gump__corpo', campo: 'lp-campo', rotulo: '', caixa: '', botao: 'lp-btn lp-btn--ouro',
    elo: 'lp-elo', erro: 'lp-aviso', miudo: 'lp-miudo'
  }
} as const;

/**
 * O código do app autenticador de uma conta da equipe (techspec-console-2fa-e-cloudflare-access §03.4): a senha
 * conferiu e a sessão só abre com os 6 números do app (ou um código de recuperação). Jogador nunca vê este passo.
 */
export function CodigoDoisFatores({ variante }: { variante: keyof typeof CLASSES }) {
  const { doisFatores, confirmarDoisFatores, cancelarDoisFatores, busy, error, clearError } = useSession();
  const c = CLASSES[variante];
  const [codigo, setCodigo] = useState('');
  const [recuperacao, setRecuperacao] = useState('');
  const [usarRec, setUsarRec] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => { campo.current?.focus(); }, [usarRec]);

  if (!doisFatores) return null;

  const pronto = usarRec ? recuperacao.replace('-', '').length === 10 : codigo.length === 6;
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !pronto) return;
    void confirmarDoisFatores(usarRec ? { recuperacao } : { codigo });
  };

  const input = usarRec ? (
    <input
      ref={campo}
      id="codigo-2fa"
      name="recuperacao"
      value={recuperacao}
      autoComplete="off"
      maxLength={11}
      placeholder="XXXXX-XXXXX"
      spellCheck={false}
      disabled={busy}
      aria-describedby={error ? 'codigo-2fa-erro' : undefined}
      onChange={e => { setRecuperacao(e.target.value.toUpperCase().replace(/[^A-Z2-7-]/g, '').slice(0, 11)); clearError(); }}
      style={{ letterSpacing: '.18em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
    />
  ) : (
    <input
      ref={campo}
      id="codigo-2fa"
      name="codigo"
      value={codigo}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      placeholder="000000"
      spellCheck={false}
      disabled={busy}
      aria-describedby={error ? 'codigo-2fa-erro' : undefined}
      onChange={e => { setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6)); clearError(); }}
      style={{ letterSpacing: '.35em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
    />
  );
  const rotulo = usarRec ? t('doisFatores.recuperacao') : t('doisFatores.codigo');

  return (
    <form className={c.form} onSubmit={enviar} noValidate>
      <p className={c.miudo} role="status">{usarRec ? t('doisFatores.textoRecuperacao') : t('doisFatores.texto')}</p>
      <div className={c.campo}>
        {c.rotulo
          ? <span className={c.rotulo}><label htmlFor="codigo-2fa">{rotulo}</label></span>
          : <label htmlFor="codigo-2fa">{rotulo}</label>}
        {c.caixa ? <span className={c.caixa}>{input}</span> : input}
      </div>
      {error && <p className={c.erro} id="codigo-2fa-erro" role="alert">{error}</p>}
      <button type="submit" className={c.botao} disabled={busy || !pronto}>
        {busy ? t('doisFatores.conferindo') : t('doisFatores.entrar')}
      </button>
      <p className={c.miudo}>
        <button type="button" className={c.elo} onClick={() => { setUsarRec(v => !v); clearError(); }} disabled={busy}>
          {usarRec ? t('doisFatores.usarApp') : t('doisFatores.usarRecuperacao')}
        </button>
        {' · '}
        <button type="button" className={c.elo} onClick={cancelarDoisFatores} disabled={busy}>
          {t('doisFatores.voltar')}
        </button>
      </p>
    </form>
  );
}
